import { Button as BaseUIButton } from '@base-ui/react/button'

import {
  hasDisabledReason,
  useDisabledReasonDescription,
} from '#components/_primitives/disabled-reason'
import { DisabledReasonTooltip } from '#components/_primitives/disabled-reason-tooltip'
import { Spinner } from '#components/spinner'
import { textVariants } from '#components/text/styles'
import { TextColor } from '#components/text/types'
import { cn } from '#lib/cn'

import { buttonSizeToTextSize, buttonVariants } from './styles'
import { ButtonSize, ButtonVariant, type InternalButtonProps } from './types'

function Button(buttonProps: InternalButtonProps) {
  const {
    className,
    leftSection,
    rightSection,
    children,
    size: sizeProp,
    isLoading,
    disabled,
    disabledReason,
    focusableWhenDisabled,
    variant = ButtonVariant.Primary,
    labelKey,
    labelStyle,
    render,
    ref,
    'aria-describedby': ariaDescribedBy,
    ...props
  } = buttonProps
  const size = sizeProp ?? ButtonSize.Medium
  const hasReason = hasDisabledReason(disabledReason)
  const { describedBy, description } = useDisabledReasonDescription(
    disabledReason,
    ariaDescribedBy,
  )
  const focusable = hasReason || (focusableWhenDisabled ?? isLoading)

  const button = (
    <BaseUIButton
      aria-describedby={describedBy}
      className={cn(buttonVariants({ className, size, variant }))}
      data-size={size}
      data-variant={variant}
      focusableWhenDisabled={focusable}
      nativeButton={render ? false : undefined}
      ref={ref}
      render={render}
      {...props}
      // After the spread: a caller passing `disabled` (including an explicit
      // `undefined`) must not be able to re-enable a button that is loading.
      disabled={disabled || isLoading || hasReason}
    >
      <span className="inline-flex items-center gap-1.5">
        {(isLoading || leftSection) && (
          <span
            className="flex items-center [&>svg]:size-3.5"
            data-section="left"
          >
            {isLoading ? (
              <Spinner size={size} variant={variant} />
            ) : (
              leftSection
            )}
          </span>
        )}

        {children && (
          <span
            className={cn(
              '[&>svg]:size-3.5',
              textVariants({
                color: TextColor.Inherit,
                size: buttonSizeToTextSize[size],
                truncate: true,
              }),
              // A lone icon is the whole content of a square button, so the
              // spinner replaces it rather than being added beside it.
              isLoading && 'has-[>svg:only-child]:hidden',
            )}
            data-label=""
            key={labelKey}
            style={labelStyle}
          >
            {children}
          </span>
        )}

        {!isLoading && rightSection && (
          <span
            className="flex items-center [&>svg]:size-3.5"
            data-section="right"
          >
            {rightSection}
          </span>
        )}
      </span>
      {description}
    </BaseUIButton>
  )

  // Keyed on the prop's presence, not its value, so the button keeps focus
  // when its reason clears.
  if (!('disabledReason' in buttonProps)) {
    return button
  }
  return (
    <DisabledReasonTooltip reason={disabledReason}>
      {button}
    </DisabledReasonTooltip>
  )
}

Button.displayName = 'Button'

export { Button }
